import { apiClient } from './client';
import { Plan } from './payments';

/**
 * Ценовые варианты и аналитика A/B-теста прайсинга (ADR 0014).
 * Эндпоинты целиком админские: клиент про варианты не знает.
 */

export type PricingVariantStatus =
  | 'draft'
  | 'active'
  | 'enrollment_stopped'
  | 'archived';

export interface PricingVariant {
  id: string;
  name: string;
  description: string | null;
  weight: number;
  is_control: boolean;
  status: PricingVariantStatus;
  activated_at: string | null;
  archived_at: string | null;
  plans?: Plan[];
  created_at: string;
  updated_at: string;
}

export interface CreatePricingVariantDto {
  name: string;
  description?: string;
  weight?: number;
  is_control?: boolean;
}

export interface UpdatePricingVariantDto extends Partial<CreatePricingVariantDto> {}

export interface ClonePlansDto {
  from_variant_id?: string;
  code_suffix: string;
  price_multiplier?: number;
}

export interface VariantFunnelRow {
  id: string;
  name: string;
  status: PricingVariantStatus;
  is_control: boolean;
  weight: number;
  share_percent: number;
  activated_at: string | null;
  assigned: number;
  transferred: number;
  users_with_orders: number;
  orders_count: number;
  payers: number;
  paid_orders: number;
  revenue: number;
  arpu: number;
  conversion: number;
  order_to_payment_rate: number;
  is_readable: boolean;
  plans: Array<{
    code: string;
    title: string;
    price: number;
    paid_orders: number;
    revenue: number;
  }>;
  platforms: Array<{
    platform: string;
    assigned: number;
    payers: number;
    revenue: number;
  }>;
}

export interface ExperimentAnalytics {
  started_at: string | null;
  days_running: number | null;
  min_payments_for_decision: number;
  totals: {
    assigned: number;
    orders_count: number;
    paid_orders: number;
    revenue: number;
    order_to_payment_rate: number;
  };
  variants: VariantFunnelRow[];
}

export const pricingVariantsApi = {
  getAll: async (): Promise<PricingVariant[]> => {
    const response = await apiClient.get<PricingVariant[]>('/pricing-variants');
    return response.data;
  },

  getAnalytics: async (): Promise<ExperimentAnalytics> => {
    const response = await apiClient.get<ExperimentAnalytics>(
      '/pricing-variants/analytics',
    );
    return response.data;
  },

  create: async (dto: CreatePricingVariantDto): Promise<PricingVariant> => {
    const response = await apiClient.post<PricingVariant>('/pricing-variants', dto);
    return response.data;
  },

  update: async (
    id: string,
    dto: UpdatePricingVariantDto,
  ): Promise<PricingVariant> => {
    const response = await apiClient.patch<PricingVariant>(
      `/pricing-variants/${id}`,
      dto,
    );
    return response.data;
  },

  clonePlans: async (id: string, dto: ClonePlansDto): Promise<Plan[]> => {
    const response = await apiClient.post<Plan[]>(
      `/pricing-variants/${id}/clone-plans`,
      dto,
    );
    return response.data;
  },

  activate: async (id: string): Promise<PricingVariant> => {
    const response = await apiClient.post<PricingVariant>(
      `/pricing-variants/${id}/activate`,
    );
    return response.data;
  },

  stopEnrollment: async (id: string): Promise<PricingVariant> => {
    const response = await apiClient.post<PricingVariant>(
      `/pricing-variants/${id}/stop-enrollment`,
    );
    return response.data;
  },

  archive: async (id: string, targetVariantId?: string): Promise<PricingVariant> => {
    const response = await apiClient.post<PricingVariant>(
      `/pricing-variants/${id}/archive`,
      targetVariantId ? { target_variant_id: targetVariantId } : {},
    );
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/pricing-variants/${id}`);
  },
};
